import type { LucideIcon } from 'lucide-react';
import { Compass, Mail, Sparkles, User, Users } from 'lucide-react';

/**
 * Where a member can go, stated once for both shells (`MEM-042` AC-2, AC-3).
 *
 * ## Why this is a module and not two arrays
 *
 * `MEM-042` AC-1 is *"one application, one account, two shells"*, and the cheapest way to break
 * that is to let the phone and the desktop keep their own lists. They drift by addition — a fifth
 * destination lands on the bar somebody was looking at, and the other chrome silently does not
 * have it. Both shells map this array, so a destination is added once or not at all.
 *
 * ## The ruling this list follows (Sumit, 2026-09-11)
 *
 * **Discover · Your Events · Messages · Connections at every width, plus You on phone** — asked
 * whether the navigation becomes that, with Matches and Follow-up moving inside Your Events as the
 * 10 September prototype draws it, he answered *"agree"*. It departs from the specification's
 * `MEM-SHL-002` and `MEM-SHL-003`, and the departure was confirmed before it was built
 * (`docs/plans/rulings-2026-09-11.md`, ruling 3). It supersedes the 2026-08-23 bar (Discover ·
 * Matches · Connections · Follow-up · You).
 *
 * **Matches and Follow-up leave the bar and keep their routes.** Mailed links pin
 * `/app/events/{id}/matches` and `/app/follow-up/events/{id}`, and the two old index addresses,
 * `/app/matches` and `/app/follow-up`, now open Your Events (`MEM-052` AC-6).
 */
export interface MemberDestination {
  readonly to: string;
  readonly label: string;
  readonly Icon: LucideIcon;
  /**
   * Whether the desktop header carries it.
   *
   * One is phone-only, for the reason AC-3 states rather than a preference: **You** is on the
   * phone because the phone has no avatar button. AC-3 puts the member's avatar at the right of
   * the desktop header, and that *is* the route to the profile — a tab as well would be two
   * controls for one destination.
   */
  readonly onDesktop: boolean;
}

/**
 * **Messages is in the navigation** — switched on 2026-09-12, in the change that built the page
 * behind it (`MEM-055` AC-1, ADR 052).
 *
 * The constant survives its own flip on purpose. It is what the plan's rule reads as — *"a
 * destination joins the bar in the change that builds its page"* (`MEM-042`'s Notes) — and it is
 * the one place `activeDestinationFor` and both chromes agree about whether the item exists. A
 * later destination added the same way has a pattern to follow, and a Messages item that had to
 * be removed for any reason is one edit rather than four.
 *
 * `MEM-042` AC-4 is reworded by `MEM-055` AC-4 in the same change: the item carries a dot while
 * any of the member's threads is unread.
 */
export const MESSAGES_IN_NAVIGATION = true;

const DISCOVER: MemberDestination = { to: '/app', label: 'Discover', Icon: Compass, onDesktop: true };

/** Your Events (`MEM-052`). The prototype's icon for it is `sparkles`. */
export const YOUR_EVENTS_DESTINATION = '/app/your-events';

const YOUR_EVENTS: MemberDestination = {
  to: YOUR_EVENTS_DESTINATION,
  label: 'Your Events',
  Icon: Sparkles,
  onDesktop: true,
};

const MESSAGES: MemberDestination = {
  to: '/app/messages',
  label: 'Messages',
  Icon: Mail,
  onDesktop: true,
};

const CONNECTIONS: MemberDestination = {
  to: '/app/connections',
  label: 'Connections',
  Icon: Users,
  onDesktop: true,
};

/**
 * **Your account** — what the desktop avatar links to and what the phone's **You** tab opens.
 *
 * It was `/app/profile` until 2026-09-12. Sumit, reviewing Phase 5: *"profile is still taking to
 * old profile page"*, and, offered a redirect, *"we don't need to redirect the old profile url, it
 * can just be removed."* So the screen is gone, the address is gone, and both doors now open the
 * one screen `Web App.dc.html` §2.15 draws. That overturns ruling 38.
 *
 * The constant keeps its name because it is the **role** — where a member goes to look at
 * themselves — and both chromes, `activeDestinationFor` and the two-shells test all name it. What
 * changed is the address behind the role, which is the one thing a constant like this exists to
 * let change in one place.
 */
export const PROFILE_DESTINATION = '/app/account';

const YOU: MemberDestination = { to: PROFILE_DESTINATION, label: 'You', Icon: User, onDesktop: false };

/**
 * The ruled order: Discover · Your Events · Messages · Connections · You. Messages is in its place
 * and drawn only once {@link MESSAGES_IN_NAVIGATION} says so.
 */
export const MEMBER_DESTINATIONS: ReadonlyArray<MemberDestination> = [
  DISCOVER,
  YOUR_EVENTS,
  ...(MESSAGES_IN_NAVIGATION ? [MESSAGES] : []),
  CONNECTIONS,
  YOU,
];

/**
 * **The three badged items**, matched by `to` so reordering either chrome cannot detach a dot.
 *
 * Sumit's answer of 2026-09-11 (`rulings-2026-09-11.md`, answer 1) put three signals where
 * `MEM-SHL-004` has one, and two of them were these:
 *
 *   - **Your Events**, while a follow-up is outstanding (`MEM-042` AC-5) — built in Phase 1;
 *   - **Messages**, while any thread is unread (`MEM-055` AC-4) — built with the page.
 *
 * His third signal was the **count** of pending requests on Connections' *Requests* control,
 * which is that page's and not a navigation badge at all.
 *
 * **Connections joined them on 2026-09-23** (`MEM-042` AC-7, TW 44159923), which **departs from
 * that ruling** and from AC-4's two badged destinations. Sumit, approving it: *"a waiting
 * connection request will show a dot on the Connections item in the navigation, so you can see it
 * from anywhere in the app."* The reason is the one the other two dots exist for: a member who is
 * not already on the Connections page has no way of learning a request arrived, and a request
 * sitting unanswered costs somebody a connection. The page's count is **unchanged** and stays
 * where the 11 September ruling put it — the dot says *there is something*, the count says how
 * many, and they are on different screens.
 *
 * All three are dots with no number. A count is a second place the total is stated and a second
 * place it can be wrong; the dot says *there is something* and the screen says what.
 */
export const BADGED_DESTINATION = YOUR_EVENTS_DESTINATION;
export const MESSAGES_DESTINATION = MESSAGES.to;
export const CONNECTIONS_DESTINATION = CONNECTIONS.to;

/**
 * **Which destination owns the page the member is on**, so the header can say where they are.
 *
 * The prototype marks an item active on its own screen and on the screens beneath it — Discover
 * on an event page; Your Events on an event's matches, connect and follow-up screens; Connections
 * on a connection — and marks **nothing** on the account pages or an organizer profile.
 * `handoff-corrections.md` #120 corrects the second half: a member on those pages cannot tell
 * where they are, so the owning destination is marked there too — You (the avatar) for the
 * account and profile pages, Discover for an organizer, whom a member reaches from an event.
 *
 * `null` for a path no destination owns, which marks nothing rather than guessing.
 *
 * Since 2026-09-12 the account pages *are* the You destination rather than a page beneath it, so
 * the branch that used to name two prefixes names one.
 */
export function activeDestinationFor(pathname: string): string | null {
  const path = pathname.replace(/\/+$/u, '') || '/';

  if (path === '/app') return DISCOVER.to;
  if (/^\/app\/events\/[^/]+\/(?:matches|connect)(?:\/|$)/u.test(path)) return YOUR_EVENTS.to;
  if (path === YOUR_EVENTS.to || startsWithSegment(path, '/app/matches')) return YOUR_EVENTS.to;
  if (startsWithSegment(path, '/app/follow-up')) return YOUR_EVENTS.to;
  if (startsWithSegment(path, '/app/events') || startsWithSegment(path, '/app/organizers')) {
    return DISCOVER.to;
  }
  if (startsWithSegment(path, MESSAGES.to)) return MESSAGES.to;
  if (startsWithSegment(path, CONNECTIONS.to)) return CONNECTIONS.to;
  if (startsWithSegment(path, PROFILE_DESTINATION)) return PROFILE_DESTINATION;
  return null;
}

/**
 * **Is this page one of the navigation's own destinations** — Discover, Your Events, Connections,
 * You (and Messages once it is switched on) — rather than a page beneath one?
 *
 * The phone's top bar is drawn on these and nowhere else (`phone-top-bar.tsx`): a page beneath a
 * destination carries its own back header, and two headers stacked on a 375px screen is one too
 * many.
 */
export function isTopLevelDestination(pathname: string): boolean {
  const path = pathname.replace(/\/+$/u, '') || '/';
  return MEMBER_DESTINATIONS.some((destination) => destination.to === path);
}

function startsWithSegment(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** Initials for the desktop avatar. Falls back to the mark rather than to an empty circle. */
export function memberInitials(displayName: string | undefined): string {
  const words = (displayName ?? '').trim().split(/\s+/u).filter(Boolean);
  if (words.length === 0) return 'H';
  if (words.length === 1) return (words[0] ?? '').slice(0, 2).toUpperCase();
  return `${(words[0] ?? '').charAt(0)}${(words[words.length - 1] ?? '').charAt(0)}`.toUpperCase();
}
